import { useState, useCallback, useRef } from 'react';

// Reads Rails' CSRF token from the `<meta name="csrf-token">` tag rendered by
// `csrf_meta_tags`. Exported for the rare non-hook contexts (adapter callbacks,
// module-level helpers) that need the token; anywhere inside a React component,
// prefer `useRailsApi()` which handles CSRF automatically.
export const getCsrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
};

const requestOptions = (method, body, signal) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  };

  const options = { method, headers, signal };

  if (body) {
    options.body = JSON.stringify(body);
  }

  return options;
};

const responseError = async (response) => {
  const errorData = await response.json().catch(() => ({}));
  // Rails typically returns `{ error: "..." }` (singular) for non-422
  // failures and `{ errors: {...} }` for 422 validation failures. Some
  // endpoints use `{ message: "..." }`. Prefer explicit strings; fall
  // back to a generic HTTP-status message.
  const message =
    (typeof errorData.error === 'string' && errorData.error) ||
    (typeof errorData.message === 'string' && errorData.message) ||
    `Request failed with status ${response.status}`;
  const err = new Error(message);
  err.status = response.status;
  err.body = errorData;
  return err;
};

export function useRailsApi() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [errorBody, setErrorBody] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Ref mirror of the latest error so callers can read it synchronously right
  // after `await api.post(...)` returns null — React state doesn't flush
  // until the next render, so the state fields captured in the current
  // closure would still be stale.
  const lastErrorRef = useRef({ message: null, status: null, body: null });

  const request = useCallback(async (url, method = 'GET', body = null, { signal } = {}) => {
    setIsLoading(true);
    setError(null);
    setErrorStatus(null);
    setErrorBody(null);
    setData(null);
    lastErrorRef.current = { message: null, status: null, body: null };

    try {
      const response = await fetch(url, requestOptions(method, body, signal));

      if (!response.ok) throw await responseError(response);

      const responseText = await response.text();
      const parsed = responseText ? JSON.parse(responseText) : null;
      setData(parsed);
      return parsed;
    } catch (err) {
      // A caller-initiated abort is not a failure; leave error state clean.
      if (err.name === 'AbortError') return null;
      setError(err.message);
      if (err.status !== undefined) setErrorStatus(err.status);
      if (err.body !== undefined) setErrorBody(err.body);
      lastErrorRef.current = {
        message: err.message,
        status: err.status ?? null,
        body: err.body ?? null,
      };
      console.error('Rails API Error:', err); // eslint-disable-line no-console
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Snapshot of the most recent request's error, readable synchronously.
  // Prefer the reactive `error` / `errorStatus` / `errorBody` fields when
  // rendering; use `getLastError()` only when you need the error info in the
  // same closure as `await api.post(...)`.
  const getLastError = useCallback(() => ({ ...lastErrorRef.current }), []);

  return {
    data,
    error,
    // On a non-2xx response, `errorStatus` holds the numeric HTTP status and
    // `errorBody` holds the parsed JSON body (or `{}` if unparseable). Both
    // reset on every new request. Useful for callers that need to
    // discriminate between 422 (validation) and other failures.
    errorStatus,
    errorBody,
    isLoading,
    getLastError,
    get: useCallback((url, options) => request(url, 'GET', null, options), [request]),
    post: useCallback((url, payload) => request(url, 'POST', payload), [request]),
    put: useCallback((url, payload) => request(url, 'PUT', payload), [request]),
    patch: useCallback((url, payload) => request(url, 'PATCH', payload), [request]),
    destroy: useCallback((url) => request(url, 'DELETE'), [request]),
  };
}
